/**
 * The card registry (public.card) and "link a website" submission.
 *
 * A row is the server's shape; GameMeta is what the carousel consumes. cardRowToMeta is the
 * only place the two meet, so the feed RPC and submit_link can return the same row and the
 * client treats a freshly linked card exactly like one that came down in the feed.
 *
 * Linking is server-checked: submit_link reads the target's headers (X-Frame-Options,
 * frame-ancestors, redirects, content-type) before it makes a card, because the feed cannot
 * tell a refused frame from a blank page. The client only tidies the input and shows the
 * server's reason when it says no.
 *
 * Since migration 0020 nothing anyone adds is live at once: submit_link and submit_media put it
 * on the bench, where it waits for a person to open it on a phone (card_set_status is the only
 * door to live). card_public serves live cards only, so a card is read back through
 * card_by_slug, which also answers its creator (and an admin) while it waits.
 */
import type { GameMeta } from '../lib/protocol';
import { ensureSession, supabase } from '../backend/supabase';
import { MOCK_BENCH, MOCK_STATUS_LOG, type MockBenchRow } from './mockBench';

export interface CardRow {
  card_id: string;
  class: 'solo' | 'live' | 'clip' | 'thread' | 'web' | 'image';
  source: 'wasm' | 'hosted' | 'external';
  title: string;
  developer: string;
  description: string;
  page_url: string | null;
  wasm_url: string | null;
  data_url: string | null;
  thumbnail_url: string | null;
  accent: string | null;
  verbs: string[];
  created_at: string;
  /** 'mirror': the site refuses framing, so it is served through /m on the mirror origin. */
  embed_mode?: 'frame' | 'mirror';
  /** Uploaded photo or video: the file; page_url is the viewer. */
  media_url?: string | null;
  source_url?: string | null;
  platform?: string | null;
  comment_count?: number;
}

/** Where a card stands (card.status). Only 'live' reaches the feed; 'bench' waits for a look on a phone. */
export type CardStatus = 'live' | 'bench' | 'hidden' | 'blocked';

/**
 * Where the mirror lives: the deploy's own second origin (Netlify's DEPLOY_URL, baked at build
 * as VITE_MIRROR_ORIGIN), so a mirrored page has an origin that is not the feed's. Without one
 * (dev, e2e) it is our own origin, and sandboxTokensFor then gives the frame an opaque origin.
 */
export const MIRROR_ORIGIN: string = ((import.meta.env.VITE_MIRROR_ORIGIN as string | undefined) || '').replace(/\/$/, '');

/**
 * The URL the iframe actually loads for a card: its page, or its page through the mirror as
 * /m/<host>/<path> — a path, not a query, so the page's own relative navigations and assets
 * resolve to further mirror paths instead of walking back to the site that refuses framing.
 */
export function frameUrlFor(meta: Pick<GameMeta, 'page_url' | 'embed_mode'>, ownOrigin: string = typeof location !== 'undefined' ? location.origin : ''): string | undefined {
  if (!meta.page_url) return undefined;
  if (meta.embed_mode !== 'mirror') return meta.page_url;
  let u: URL;
  try {
    u = new URL(meta.page_url);
  } catch {
    return meta.page_url;
  }
  return `${MIRROR_ORIGIN || ownOrigin}/m/${u.hostname}${u.pathname}${u.search}`;
}

export function cardRowToMeta(r: CardRow): GameMeta {
  return {
    id: r.card_id,
    kind: r.source === 'wasm' ? 'wasm' : 'web',
    source: r.embed_mode === 'mirror' ? 'external' : r.source,
    class: r.class,
    embed_mode: r.embed_mode === 'mirror' ? 'mirror' : undefined,
    media_url: r.media_url ?? undefined,
    source_url: r.source_url ?? undefined,
    platform: r.platform ?? undefined,
    comment_count: r.comment_count ?? 0,
    title: r.title,
    developer: r.developer,
    wasm_url: r.wasm_url ?? '',
    page_url: r.page_url ?? undefined,
    data_url: r.data_url,
    thumbnail_url: r.thumbnail_url ?? '',
    verbs: r.verbs ?? [],
    accent: r.accent ?? undefined,
    description: r.description || undefined,
  };
}

/**
 * What a person types is rarely a URL: "deepstate-demo.netlify.app" is the common case. Add
 * the scheme, refuse plain http (the feed is https and a mixed-content frame is blocked
 * anyway), and leave everything else to the server's check.
 */
export function normalizeLinkInput(raw: string): { url: string } | { error: string } {
  const s = raw.trim();
  if (!s) return { error: 'Paste a link first.' };
  if (/^http:\/\//i.test(s)) return { error: 'Needs an https:// address — an http page cannot be shown inside an https feed.' };
  const withScheme = /^https:\/\//i.test(s) ? s : `https://${s}`;
  let u: URL;
  try {
    u = new URL(withScheme);
  } catch {
    return { error: 'That does not look like a web address.' };
  }
  if (u.username || u.password) return { error: 'Links with a username or password are not allowed.' };
  // Chromium's parser accepts hosts Node's rejects ("not a url" becomes not%20a%20url), so the
  // hostname is checked explicitly, with the same shape the server's embed_check demands.
  if (!HOSTNAME.test(u.hostname)) return { error: 'That does not look like a web address.' };
  u.hash = '';
  return { url: u.toString() };
}

/** A public-looking hostname: labels of letters, digits and hyphens, at least one dot. */
const HOSTNAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

export type LinkResult =
  | { ok: true; meta: GameMeta; existing: boolean; status: CardStatus }
  | { ok: false; reason: string };

/** What the server says after an add, before the card is read back. */
export interface AddAnswer {
  ok?: boolean;
  card_id?: string;
  status?: CardStatus;
  existing?: boolean;
  reason?: string;
  code?: string;
}

/**
 * Read back the card an add answered with. A new card is the caller's and on the bench, which
 * card_by_slug answers for its creator. An existing one may be someone else's page still waiting
 * for its look; card_by_slug answers nobody else for that, and the person is told so in words.
 */
export async function readBackAdded(r: AddAnswer, noun: 'page' | 'file'): Promise<LinkResult> {
  const status: CardStatus = r.status ?? 'bench';
  const meta = r.card_id ? await cardBySlug(r.card_id) : null;
  if (meta) return { ok: true, meta, existing: Boolean(r.existing), status };
  if (r.existing && status === 'bench') {
    return {
      ok: false,
      reason:
        noun === 'page'
          ? 'Someone has already added that page. It joins the feed once it has been looked at on a phone.'
          : 'That exact file is already on Blitz. It joins the feed once it has been looked at on a phone.',
    };
  }
  return { ok: false, reason: 'Added, but the card could not be read back. It is on your profile.' };
}

/**
 * What a person is told once something they added comes back. Nothing is live at once since
 * migration 0020: a new card is next for its maker and joins everyone's feed after a person has
 * opened it on a phone, and the words say exactly that — never 'it is live'.
 */
export function addedCopy(o: { handle: string | null; developer: string; existing: boolean; status: CardStatus }): string {
  const h = o.handle ?? o.developer;
  if (o.status === 'live') return o.existing ? 'Already on Blitz — it is next.' : `On Blitz under @${h} — it is next.`;
  if (o.existing && o.developer !== o.handle) {
    return `Already added by @${o.developer}. It is next for you; everyone else sees it once it has been looked at on a phone.`;
  }
  return `Added under @${h}. It is next for you; everyone else sees it once it has been looked at on a phone — usually today.`;
}

/** Link a website as a card. Never throws; every failure is a reason a person can read. */
export async function submitLink(raw: string): Promise<LinkResult> {
  const n = normalizeLinkInput(raw);
  if ('error' in n) return { ok: false, reason: n.error };
  if (!supabase) return { ok: false, reason: 'This build has no backend, so links cannot be saved.' };
  const session = await ensureSession();
  if (!session) return { ok: false, reason: 'Could not start a session — try again in a moment.' };

  const { data, error } = await supabase.rpc('submit_link', { url: n.url });
  if (error) {
    if (error.code === '53400') return { ok: false, reason: 'That is a lot of links for one hour. Try again later.' };
    return { ok: false, reason: error.message || 'The server refused the link.' };
  }
  const r = (data ?? {}) as AddAnswer;
  if (!r.ok || !r.card_id) return { ok: false, reason: r.reason ?? 'Could not link that page.' };
  return readBackAdded(r, 'page');
}

/**
 * Where "Open" takes a visitor: the site itself, or the page a platform card was pasted from.
 * Only a site on another origin — never one of our own viewers. An uploaded page opened
 * top-level would put a stranger's content full-window under our address bar (a login sheet
 * drawn by a page reads as ours), so uploads get no Open.
 */
export function openUrlFor(meta: Pick<GameMeta, 'kind' | 'page_url' | 'source_url'>, ownOrigin: string = typeof location !== 'undefined' ? location.origin : 'https://blitzfeed.app'): string | null {
  if (meta.kind !== 'web') return null;
  const target = meta.source_url || meta.page_url;
  if (!target) return null;
  try {
    const u = new URL(target, ownOrigin);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (u.origin === ownOrigin) return null;
    return u.href;
  } catch {
    return null;
  }
}

/**
 * One card by its link (the slug is the card id), through card_by_slug: a live card for anyone;
 * a card on the bench for its creator or an admin, and for anyone once the fit check has passed
 * it; nothing for a hidden or blocked card, an unknown slug, or a build with no backend. No
 * session is started for it: a stranger opening a share link reads as anon, and a maker's own
 * persisted session is sent when there is one.
 */
export async function cardBySlug(slug: string): Promise<GameMeta | null> {
  if (!supabase || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(slug)) return null;
  try {
    const { data, error } = await supabase.rpc('card_by_slug', { p_slug: slug });
    if (error) return null;
    const row = Array.isArray(data) ? (data[0] as CardRow | undefined) : (data as CardRow | null);
    return row ? cardRowToMeta(row) : null;
  } catch {
    return null;
  }
}

/** One of the caller's own cards, as my_cards() returns it: the card, and where it stands. */
export interface MyCard {
  meta: GameMeta;
  status: CardStatus;
  /** The fit check's verdict: true, false, or null while it has not run. */
  fitOk: boolean | null;
  /** The fit check's one phrase ('fits', 'wider than a phone', ...), when it has run. */
  fitSummary: string | null;
  /** The maker's own 'Made with' word, never verified. */
  madeWith: string | null;
  /** The latest status log reason ('removed by maker', 'reviewed: phone', ...). */
  latestReason: string | null;
}

/**
 * Where one of your cards stands, in a few words. Since migration 0020 a card waits on the bench
 * until a person has opened it on a phone; the fit check's phrase rides along once it has run.
 */
export function cardStanding(c: Pick<MyCard, 'status' | 'fitSummary' | 'latestReason'>): string {
  switch (c.status) {
    case 'live':
      return 'live';
    case 'bench':
      return c.fitSummary ? `waiting for a look · ${c.fitSummary}` : 'waiting for a look';
    case 'hidden':
      return c.latestReason === 'removed by maker' || !c.latestReason ? 'taken down' : 'hidden';
    default:
      return 'not available';
  }
}

/**
 * A card its maker took down (remove_own_card: status hidden, the log reads 'removed by maker';
 * a row from before the log has no reason). The profile folds these under one line, collapsed,
 * so a maker's list is what is up and waiting, not everything they ever tried (the founder's
 * ruling, 27 Sep). A card the reviewer hid stays in the list: its reason is feedback.
 */
export const isTakenDown = (c: Pick<MyCard, 'status' | 'fitSummary' | 'latestReason'>): boolean => cardStanding(c) === 'taken down';

type MyCardRow = CardRow & {
  status?: CardStatus;
  fit_ok?: boolean | null;
  fit_summary?: string | null;
  made_with?: string | null;
  latest_reason?: string | null;
};

const myCardOf = (r: MyCardRow): MyCard => ({
  meta: cardRowToMeta(r),
  // A server from before migration 0020 lists live cards only, with no status column.
  status: r.status ?? 'live',
  fitOk: r.fit_ok ?? null,
  fitSummary: r.fit_summary ?? null,
  madeWith: r.made_with ?? null,
  latestReason: r.latest_reason ?? null,
});

/**
 * The caller's own cards in every status, newest first. Empty without an account. The build with
 * no backend answers window.__blitzFixture.mine when the e2e suite hands it rows (my_cards' row
 * shape), as /home honours .home, and nothing otherwise.
 */
export async function myCards(): Promise<MyCard[]> {
  if (!supabase) {
    const rows = typeof window === 'undefined' ? undefined : (window as unknown as { __blitzFixture?: { mine?: MyCardRow[] } }).__blitzFixture?.mine;
    return Array.isArray(rows) ? rows.map(myCardOf) : [];
  }
  const session = await ensureSession();
  if (!session) return [];
  const { data, error } = await supabase.rpc('my_cards');
  if (error || !Array.isArray(data)) return [];
  return (data as MyCardRow[]).map(myCardOf);
}

/** A card waiting on the bench, as the review strip shows it (bench_feed(), migration 0020). */
export interface BenchCard {
  meta: GameMeta;
  /** The maker's handle; null for a card with no maker account behind it. */
  handle: string | null;
  /** The maker's own 'Made with' word, never verified. */
  madeWith: string | null;
  /** The fit check's one phrase, or null while it has not run. */
  fitSummary: string | null;
  fitOk: boolean | null;
}

function toBenchCard(r: MockBenchRow): BenchCard {
  return {
    meta: cardRowToMeta(r),
    handle: r.handle ?? null,
    madeWith: r.generation?.made_with ?? null,
    fitSummary: r.fit?.summary ?? null,
    fitOk: r.fit_ok ?? null,
  };
}

/**
 * The bench, oldest first, for an admin reviewing it on a phone (/feed?bench=1). Null when this is
 * not an admin (bench_feed raises 42501) or the read failed: the caller shows the normal feed. The
 * mock's three cards without a backend.
 */
export async function benchFeed(): Promise<BenchCard[] | null> {
  if (!supabase) return MOCK_BENCH.map(toBenchCard);
  const session = await ensureSession();
  if (!session) return null;
  try {
    const { data, error } = await supabase.rpc('bench_feed');
    if (error || !Array.isArray(data)) return null;
    return (data as MockBenchRow[]).map(toBenchCard);
  } catch {
    return null;
  }
}

export type SetStatusResult = { ok: true } | { ok: false; reason: string };

/**
 * Flip a card through card_set_status, the one door to live (admin only). Its trigger logs the
 * flip with via='rpc', this actor and `reason`. Without a backend the call is recorded in
 * MOCK_STATUS_LOG, which the e2e suite reads.
 */
export async function setCardStatus(cardId: string, status: 'live' | 'hidden', reason: string): Promise<SetStatusResult> {
  if (!supabase) {
    MOCK_STATUS_LOG.push({ card_id: cardId, status, reason });
    return { ok: true };
  }
  const session = await ensureSession();
  if (!session) return { ok: false, reason: 'Not signed in. Sign in as an admin and try again.' };
  try {
    const { data, error } = await supabase.rpc('card_set_status', { p_card_id: cardId, p_status: status, p_reason: reason });
    if (error) return { ok: false, reason: error.code === '42501' ? 'Only an admin can do that.' : error.message };
    const r = (data ?? {}) as { ok?: boolean; reason?: string };
    return r.ok ? { ok: true } : { ok: false, reason: r.reason ?? 'The server refused that.' };
  } catch {
    return { ok: false, reason: 'Could not reach the server. Try again.' };
  }
}

export type RemoveResult = { ok: true } | { ok: false; reason: string };

/** Public files in the media bucket live under this address (mirrors storage_base() in migration 0013). */
const MEDIA_BASE = `${(import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? ''}/storage/v1/object/public/media/`;

/**
 * Take your own card down. The card is hidden (comments and telemetry stay; linking the same
 * address again restores it); an uploaded file is removed from the bucket too, so the address
 * stops serving it — that part is not undone by re-linking.
 */
export async function removeOwnCard(meta: Pick<GameMeta, 'id' | 'media_url'>): Promise<RemoveResult> {
  if (!supabase) return { ok: false, reason: 'This build has no backend.' };
  const session = await ensureSession();
  if (!session) return { ok: false, reason: 'Could not start a session — try again in a moment.' };
  const { data, error } = await supabase.rpc('remove_own_card', { p_card_id: meta.id });
  if (error) return { ok: false, reason: error.message || 'Could not take that down.' };
  const r = (data ?? {}) as { ok?: boolean; reason?: string };
  if (!r.ok) return { ok: false, reason: r.reason ?? 'Could not take that down.' };
  if (meta.media_url && MEDIA_BASE.length > 40 && meta.media_url.startsWith(MEDIA_BASE)) {
    try {
      await supabase.storage.from('media').remove([meta.media_url.slice(MEDIA_BASE.length)]);
    } catch {
      /* the card is already down; a file that lingers is the storage policy's problem, not the person's */
    }
  }
  return { ok: true };
}

// ---------------------------------------------------------------- a maker's numbers (card_stats, migration 20260930090000)

/**
 * What the host measured about one of your cards over the last `windowDays` days: sessions
 * (impressions), distinct people (viewers), sessions of five seconds or more (held), sessions that
 * ended on BLITZ (blitzPast), live comments in the window, and the first impression ever. Nothing
 * a card or a client reported about itself is in here, and neither is the like count — a like is
 * a bookmark (docs/RANKING.md). Your own sessions on this device are left out; a second device
 * of yours counts as a stranger (the cross-device limitation, docs/CONTENT.md).
 */
export interface CardStats {
  cardId: string;
  impressions: number;
  viewers: number;
  held: number;
  blitzPast: number;
  comments: number;
  since: string | null;
  windowDays: number;
}

interface CardStatsRow {
  card_id: string;
  impressions: number | null;
  viewers: number | null;
  held: number | null;
  blitz_past: number | null;
  comments: number | null;
  since: string | null;
  window_days: number | null;
}

/** Your cards' numbers, keyed by card id. Empty without a backend, a session, or cards; empty when the call fails. */
export async function cardStats(days = 14): Promise<Map<string, CardStats>> {
  const out = new Map<string, CardStats>();
  if (!supabase) return out;
  const session = await ensureSession();
  if (!session) return out;
  const { data, error } = await supabase.rpc('card_stats', { days });
  if (error || !Array.isArray(data)) return out;
  for (const r of data as CardStatsRow[]) {
    if (!r || typeof r.card_id !== 'string') continue;
    out.set(r.card_id, {
      cardId: r.card_id,
      impressions: r.impressions ?? 0,
      viewers: r.viewers ?? 0,
      held: r.held ?? 0,
      blitzPast: r.blitz_past ?? 0,
      comments: r.comments ?? 0,
      since: r.since ?? null,
      windowDays: r.window_days ?? days,
    });
  }
  return out;
}

/**
 * A card's numbers as one sentence — "3 people, 2 stayed" is the truth and worth more to a maker
 * than a bigger number that is not (docs/CONTENT.md). Opens are named only when people opened
 * more than once; BLITZ-past and comments only when there were any.
 */
export function statsSentence(s: Pick<CardStats, 'impressions' | 'viewers' | 'held' | 'blitzPast' | 'comments'>): string {
  if (s.impressions <= 0) return 'No one has opened it yet.';
  const people = `${s.viewers} ${s.viewers === 1 ? 'person' : 'people'}`;
  const parts = [s.impressions > s.viewers ? `${s.impressions} opens by ${people}` : people, `${s.held} stayed`];
  if (s.blitzPast > 0) parts.push(`${s.blitzPast} pressed on`);
  if (s.comments > 0) parts.push(`${s.comments} ${s.comments === 1 ? 'comment' : 'comments'}`);
  return `${parts.join(', ')}.`;
}

/**
 * A maker opened their numbers: the last step of the creation loop (loop_funnel, docs/SPRINT-2.md
 * section 2), one ui_event row per page view. Only ever with the session that already exists —
 * a page that shows numbers has one — so this never mints anyone. Fire-and-forget.
 */
export function logStatsOpen(): void {
  if (!supabase) return;
  const client = supabase;
  void client.auth
    .getSession()
    .then(({ data }) => {
      if (!data.session) return;
      return client.rpc('log_ui', { p_kind: 'stats_open', p_card_id: null, p_visit_id: null, p_feed_position: null });
    })
    .catch(() => {
      /* a lost press is a lost press */
    });
}
